import {
  DeviceInfo,
  DeviceOS,
  ClipItem,
  ClipMetadata,
  ClipType,
  ConnectionStatus,
  RelayMessage,
  UserSettings
} from './types';
import { encryptPayload, decryptPayload } from './crypto';
import { sound } from './sound';
import { NostrRelayPool, NostrEvent } from './nostrRelay';

export interface SyncEngineCallbacks {
  onStatusChange: (status: ConnectionStatus) => void;
  onPeersChange: (peers: DeviceInfo[]) => void;
  onClipReceived: (clip: ClipItem) => void;
  onRateLimited: (message: string, retryAfter: number) => void;
  onError: (error: string) => void;
}

const ICE_SERVERS: RTCIceServer[] = [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'stun:stun1.l.google.com:19302' }
];

export class SyncEngine {
  private ws: WebSocket | null = null;
  private nostrPool: NostrRelayPool;
  private roomId: string = '';
  private accessCode: string = '';
  private device: DeviceInfo;
  private settings: UserSettings;
  private callbacks: SyncEngineCallbacks;
  private status: ConnectionStatus = 'disconnected';
  private pingInterval: any = null;
  private reconnectTimeout: any = null;
  private reconnectAttempts = 0;
  private peers: Map<string, DeviceInfo> = new Map();
  private lastCopiedText: string = '';
  private lastPingSentTime: number = 0;
  private receivedClipIds: Set<string> = new Set();

  // WebRTC P2P DataChannels
  private peerConnections: Map<string, RTCPeerConnection> = new Map();
  private dataChannels: Map<string, RTCDataChannel> = new Map();

  constructor(settings: UserSettings, callbacks: SyncEngineCallbacks) {
    this.settings = settings;
    this.callbacks = callbacks;
    this.device = this.initDeviceInfo();
    this.nostrPool = new NostrRelayPool();

    if (typeof window !== 'undefined') {
      window.addEventListener('focus', () => this.flushPendingClipboard());
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
          this.flushPendingClipboard();
        }
      });
      // Request notification permission if not asked
      if ('Notification' in window && Notification.permission === 'default') {
        Notification.requestPermission().catch(() => {});
      }
    }
  }

  private pendingClipboardText: string | null = null;

  public async flushPendingClipboard() {
    if (this.pendingClipboardText && this.settings.autoSyncToClipboard && typeof navigator !== 'undefined' && navigator.clipboard) {
      try {
        await navigator.clipboard.writeText(this.pendingClipboardText);
        this.lastCopiedText = this.pendingClipboardText;
        const text = this.pendingClipboardText;
        this.pendingClipboardText = null;
        console.log('[SyncEngine] Auto-synced pending clip on window focus:', text.slice(0, 30));
      } catch (err) {
        console.warn('[SyncEngine] Failed to auto-write clipboard on focus:', err);
      }
    }
  }

  private initDeviceInfo(): DeviceInfo {
    let deviceId = localStorage.getItem('ck_device_id');
    if (!deviceId) {
      deviceId = 'dev_' + Math.random().toString(36).substring(2, 10);
      localStorage.setItem('ck_device_id', deviceId);
    }

    const os = this.detectOS();
    let deviceName = localStorage.getItem('ck_device_name');
    if (!deviceName) {
      const osNames: Record<DeviceOS, string> = {
        android: 'Android Phone',
        windows: 'Windows PC',
        mac: 'MacBook',
        linux: 'Linux Workstation',
        ios: 'iPhone',
        web: 'Web Browser',
        extension: 'Browser Extension'
      };
      deviceName = `${osNames[os]} (${deviceId.slice(-4).toUpperCase()})`;
      localStorage.setItem('ck_device_name', deviceName);
    }

    return {
      deviceId,
      deviceName,
      os,
      clientType: 'web',
      joinedAt: Date.now(),
      lastPing: Date.now()
    };
  }

  private detectOS(): DeviceOS {
    if (typeof navigator === 'undefined') return 'web';
    const ua = navigator.userAgent.toLowerCase();
    if (ua.includes('android')) return 'android';
    if (ua.includes('iphone') || ua.includes('ipad') || ua.includes('ipod')) return 'ios';
    if (ua.includes('win')) return 'windows';
    if (ua.includes('mac')) return 'mac';
    if (ua.includes('linux')) return 'linux';
    return 'web';
  }

  public detectClipType(content: string): ClipType {
    const trimmed = content.trim();
    if (/^https?:\/\/[^\s/$.?#].[^\s]*$/i.test(trimmed)) {
      return 'url';
    }
    if (/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3}|[A-Fa-f0-9]{8})$/.test(trimmed)) {
      return 'color';
    }
    if (
      trimmed.startsWith('data:image/') ||
      trimmed.startsWith('blob:')
    ) {
      return 'image';
    }
    if (
      trimmed.includes('const ') ||
      trimmed.includes('let ') ||
      trimmed.includes('var ') ||
      trimmed.includes('function') ||
      trimmed.includes('import ') ||
      trimmed.includes('export ') ||
      trimmed.includes('class ') ||
      trimmed.includes('def ') ||
      trimmed.includes('public static void') ||
      trimmed.includes('{') && trimmed.includes('}') ||
      trimmed.includes('</') ||
      trimmed.split('\n').length > 3
    ) {
      return 'code';
    }
    return 'text';
  }

  public getDevice(): DeviceInfo {
    return this.device;
  }

  public updateDeviceName(newName: string) {
    this.device.deviceName = newName;
    localStorage.setItem('ck_device_name', newName);
  }

  public updateSettings(newSettings: Partial<UserSettings>) {
    this.settings = { ...this.settings, ...newSettings };
    sound.setEnabled(this.settings.soundEnabled);
  }

  public connect(roomId: string, accessCode: string) {
    this.disconnect();
    this.roomId = roomId.trim().toUpperCase();
    this.accessCode = accessCode.trim();

    if (!this.roomId || !this.accessCode) {
      this.callbacks.onError('Room ID and Access Code are required');
      return;
    }

    this.setStatus('connecting');

    // Build WebSocket URL
    let baseRelay = this.settings.relayUrl ? this.settings.relayUrl.trim() : '';
    if (!baseRelay) {
      if (typeof window !== 'undefined') {
        const isCapacitor = typeof (window as any).Capacitor !== 'undefined' && typeof (window as any).Capacitor.isNativePlatform === 'function' && (window as any).Capacitor.isNativePlatform();
        const host = window.location.hostname;
        const port = window.location.port;

        if (isCapacitor) {
          baseRelay = 'wss://ck-share-relay.chintankas1.workers.dev';
        } else if ((host === 'localhost' || host === '127.0.0.1') && port === '5173') {
          baseRelay = 'ws://localhost:8787';
        } else {
          baseRelay = 'wss://ck-share-relay.chintankas1.workers.dev';
        }
      } else {
        baseRelay = 'wss://ck-share-relay.chintankas1.workers.dev';
      }
    }

    // Clean wsUrl
    let wsUrl = baseRelay.replace(/^http/, 'ws').replace(/\/$/, '');
    if (!wsUrl.includes('/ws/room/')) {
      wsUrl += `/ws/room/${encodeURIComponent(this.roomId)}`;
    }

    // Start Native Android background sync service trigger if available
    if (typeof (window as any).Capacitor !== 'undefined' && (window as any).Capacitor.Plugins?.CKClipboardPlugin) {
      try {
        (window as any).Capacitor.Plugins.CKClipboardPlugin.startBackgroundSync({
          roomId: this.roomId,
          accessCode: this.accessCode,
          relayUrl: baseRelay
        });
        console.log('[SyncEngine] Started native Android background sync service for room:', this.roomId);
      } catch (e) {
        console.warn('[SyncEngine] Error starting native background sync:', e);
      }
    }

    // Connect to Decentralized Nostr Fallback Pool (Layer 2)
    try {
      this.nostrPool.connect(this.roomId, async (event: NostrEvent) => {
        try {
          const parsed = JSON.parse(event.content);
          if (parsed && parsed.metadata && parsed.encrypted) {
            await this.handleIncomingClip(parsed);
          }
        } catch {
          // ignore invalid Nostr payload
        }
      });
    } catch (e) {
      console.warn('[SyncEngine] Nostr pool connect error:', e);
    }

    // Connect to Primary Cloudflare Hibernating WebSocket Relay (Layer 1)
    try {
      this.ws = new WebSocket(wsUrl);

      this.ws.onopen = () => {
        this.reconnectAttempts = 0;
        this.setStatus('connected');
        this.sendJoinRoom();
        this.startHeartbeat();
      };

      this.ws.onmessage = async (event) => {
        try {
          const raw = typeof event.data === 'string' ? event.data : new TextDecoder().decode(event.data);
          const msg: RelayMessage = JSON.parse(raw);
          await this.handleMessage(msg);
        } catch (err: any) {
          console.error('[SyncEngine] Error parsing message:', err);
        }
      };

      this.ws.onclose = () => {
        this.cleanupSocket();
        if (this.status !== 'disconnected') {
          this.scheduleReconnect();
        }
      };

      this.ws.onerror = (err) => {
        console.error('[SyncEngine] WebSocket error:', err);
        this.callbacks.onError('WebSocket connection failed. Ensure relay is running.');
      };
    } catch (err: any) {
      this.setStatus('error');
      this.callbacks.onError(err.message || 'Failed to establish WebSocket');
      this.scheduleReconnect();
    }
  }

  public disconnect() {
    this.cleanupSocket();
    this.cleanupWebRTC();
    this.nostrPool.disconnect();
    this.setStatus('disconnected');
    this.peers.clear();
    this.callbacks.onPeersChange([]);

    if (typeof (window as any).Capacitor !== 'undefined' && (window as any).Capacitor.Plugins?.CKClipboardPlugin) {
      try {
        (window as any).Capacitor.Plugins.CKClipboardPlugin.stopBackgroundSync();
      } catch {}
    }
  }

  private cleanupSocket() {
    if (this.pingInterval) clearInterval(this.pingInterval);
    if (this.reconnectTimeout) clearTimeout(this.reconnectTimeout);
    if (this.ws) {
      try {
        if (this.ws.readyState === WebSocket.OPEN) {
          this.ws.send(JSON.stringify({ type: 'LEAVE_ROOM', roomId: this.roomId, timestamp: Date.now() }));
        }
        this.ws.close();
      } catch {}
      this.ws = null;
    }
  }

  private cleanupWebRTC() {
    for (const pc of this.peerConnections.values()) {
      try { pc.close(); } catch {}
    }
    this.peerConnections.clear();
    this.dataChannels.clear();
  }

  private scheduleReconnect() {
    this.setStatus('reconnecting');
    const delay = Math.min(1000 * Math.pow(1.5, this.reconnectAttempts), 10000);
    this.reconnectAttempts++;
    this.reconnectTimeout = setTimeout(() => {
      if (this.roomId && this.accessCode) {
        this.connect(this.roomId, this.accessCode);
      }
    }, delay);
  }

  private setStatus(s: ConnectionStatus) {
    this.status = s;
    this.callbacks.onStatusChange(s);
  }

  private sendJoinRoom() {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    const msg = {
      type: 'JOIN_ROOM',
      roomId: this.roomId,
      device: this.device,
      timestamp: Date.now()
    };
    this.ws.send(JSON.stringify(msg));
  }

  private startHeartbeat() {
    // 30s interval: optimal for Cloudflare Hibernation
    this.pingInterval = setInterval(() => {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        this.lastPingSentTime = performance.now();
        this.ws.send(
          JSON.stringify({
            type: 'DEVICE_PING',
            roomId: this.roomId,
            deviceId: this.device.deviceId,
            timestamp: Date.now()
          })
        );
      }
    }, 30000);
  }

  private async handleMessage(msg: RelayMessage) {
    switch (msg.type) {
      case 'AUTH_ACK': {
        break;
      }

      case 'PEER_LIST': {
        this.peers.clear();
        if (Array.isArray(msg.peers)) {
          msg.peers.forEach((p: DeviceInfo) => {
            if (p.deviceId !== this.device.deviceId) {
              this.peers.set(p.deviceId, p);
              if (this.settings.enableWebRTC) {
                this.initiateWebRTCConnection(p.deviceId, true);
              }
            }
          });
        }
        this.callbacks.onPeersChange(Array.from(this.peers.values()));
        break;
      }

      case 'PEER_JOINED': {
        if (msg.peer && msg.peer.deviceId !== this.device.deviceId) {
          this.peers.set(msg.peer.deviceId, msg.peer);
          this.callbacks.onPeersChange(Array.from(this.peers.values()));
          sound.playDeviceJoined();

          if (this.settings.enableWebRTC) {
            this.initiateWebRTCConnection(msg.peer.deviceId, false);
          }
        }
        break;
      }

      case 'PEER_LEFT': {
        if (msg.deviceId) {
          this.peers.delete(msg.deviceId);
          this.closePeerConnection(msg.deviceId);
          this.callbacks.onPeersChange(Array.from(this.peers.values()));
        }
        break;
      }

      case 'DEVICE_PONG': {
        if (this.lastPingSentTime) {
          const latency = Math.round(performance.now() - this.lastPingSentTime);
          this.device.latencyMs = latency;
        }
        break;
      }

      case 'WEBRTC_SIGNAL': {
        if (this.settings.enableWebRTC && msg.targetDeviceId === this.device.deviceId) {
          await this.handleWebRTCSignal(msg);
        }
        break;
      }

      case 'CLIPBOARD_BROADCAST':
      case 'QUICK_DROP_BROADCAST': {
        await this.handleIncomingClip(msg);
        break;
      }

      case 'RATE_LIMIT_EXCEEDED': {
        sound.playError();
        this.callbacks.onRateLimited(msg.message, msg.retryAfterSeconds);
        break;
      }

      case 'ERROR': {
        sound.playError();
        this.callbacks.onError(msg.message || 'Relay error');
        break;
      }
    }
  }

  // --- WebRTC P2P DataChannels (Layer 3) ---

  private async initiateWebRTCConnection(targetDeviceId: string, isInitiator: boolean) {
    if (typeof RTCPeerConnection === 'undefined') return;
    if (this.peerConnections.has(targetDeviceId)) return;

    try {
      const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });
      this.peerConnections.set(targetDeviceId, pc);

      pc.onicecandidate = (e) => {
        if (e.candidate && this.ws && this.ws.readyState === WebSocket.OPEN) {
          this.ws.send(
            JSON.stringify({
              type: 'WEBRTC_SIGNAL',
              roomId: this.roomId,
              senderDeviceId: this.device.deviceId,
              targetDeviceId,
              signalType: 'candidate',
              candidate: e.candidate,
              timestamp: Date.now()
            })
          );
        }
      };

      if (isInitiator) {
        const dc = pc.createDataChannel('ckshare_sync');
        this.setupDataChannel(targetDeviceId, dc);

        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);

        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
          this.ws.send(
            JSON.stringify({
              type: 'WEBRTC_SIGNAL',
              roomId: this.roomId,
              senderDeviceId: this.device.deviceId,
              targetDeviceId,
              signalType: 'offer',
              sdp: offer,
              timestamp: Date.now()
            })
          );
        }
      } else {
        pc.ondatachannel = (e) => {
          this.setupDataChannel(targetDeviceId, e.channel);
        };
      }
    } catch (err) {
      console.warn('[SyncEngine] WebRTC init failed:', err);
    }
  }

  private setupDataChannel(targetDeviceId: string, dc: RTCDataChannel) {
    this.dataChannels.set(targetDeviceId, dc);

    dc.onopen = () => {
      console.log(`[SyncEngine] WebRTC P2P DataChannel OPEN with ${targetDeviceId}`);
    };

    dc.onmessage = async (event) => {
      try {
        const parsed = JSON.parse(event.data);
        if (parsed && parsed.metadata && parsed.encrypted) {
          await this.handleIncomingClip(parsed);
        }
      } catch (err) {
        console.error('[SyncEngine] P2P DataChannel parse error:', err);
      }
    };

    dc.onclose = () => {
      this.dataChannels.delete(targetDeviceId);
    };
  }

  private async handleWebRTCSignal(msg: any) {
    const senderDeviceId = msg.senderDeviceId;
    let pc = this.peerConnections.get(senderDeviceId);

    try {
      if (msg.signalType === 'offer') {
        if (!pc) {
          pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });
          this.peerConnections.set(senderDeviceId, pc);

          pc.onicecandidate = (e) => {
            if (e.candidate && this.ws && this.ws.readyState === WebSocket.OPEN) {
              this.ws.send(
                JSON.stringify({
                  type: 'WEBRTC_SIGNAL',
                  roomId: this.roomId,
                  senderDeviceId: this.device.deviceId,
                  targetDeviceId: senderDeviceId,
                  signalType: 'candidate',
                  candidate: e.candidate,
                  timestamp: Date.now()
                })
              );
            }
          };

          pc.ondatachannel = (e) => {
            this.setupDataChannel(senderDeviceId, e.channel);
          };
        }

        await pc.setRemoteDescription(new RTCSessionDescription(msg.sdp));
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);

        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
          this.ws.send(
            JSON.stringify({
              type: 'WEBRTC_SIGNAL',
              roomId: this.roomId,
              senderDeviceId: this.device.deviceId,
              targetDeviceId: senderDeviceId,
              signalType: 'answer',
              sdp: answer,
              timestamp: Date.now()
            })
          );
        }
      } else if (msg.signalType === 'answer' && pc) {
        await pc.setRemoteDescription(new RTCSessionDescription(msg.sdp));
      } else if (msg.signalType === 'candidate' && pc && msg.candidate) {
        await pc.addIceCandidate(new RTCIceCandidate(msg.candidate));
      }
    } catch (err) {
      console.warn('[SyncEngine] Error handling WebRTC signal:', err);
    }
  }

  private closePeerConnection(deviceId: string) {
    const pc = this.peerConnections.get(deviceId);
    if (pc) {
      try { pc.close(); } catch {}
      this.peerConnections.delete(deviceId);
    }
    this.dataChannels.delete(deviceId);
  }

  // --- Core Clipboard Handling & Decryption ---

  private async handleIncomingClip(msg: any) {
    if (this.settings.pauseSync) {
      console.log('[SyncEngine] Sync is paused by user, ignoring remote clip');
      return;
    }

    const clipId = msg.metadata?.id || '';
    if (clipId && this.receivedClipIds.has(clipId)) {
      return; // Already processed
    }
    if (clipId) {
      this.receivedClipIds.add(clipId);
      if (this.receivedClipIds.size > 200) {
        const arr = Array.from(this.receivedClipIds);
        this.receivedClipIds = new Set(arr.slice(-100));
      }
    }

    const senderId = msg.metadata?.senderDeviceId || '';
    const isNativeAndroid = typeof (window as any).Capacitor !== 'undefined' && typeof (window as any).Capacitor.isNativePlatform === 'function' && (window as any).Capacitor.isNativePlatform();
    if (senderId === this.device.deviceId || (isNativeAndroid && senderId.startsWith('dev_android_'))) {
      return;
    }

    try {
      const decryptedText = await decryptPayload(msg.encrypted, this.accessCode);

      const clipItem: ClipItem = {
        id: clipId || 'clip_' + Date.now(),
        metadata: msg.metadata,
        content: decryptedText,
        receivedAt: Date.now(),
        isLocal: false,
        isPinned: false
      };

      this.callbacks.onClipReceived(clipItem);
      sound.playSyncSuccess();

      // Auto-sync to OS clipboard if enabled
      if (this.settings.autoSyncToClipboard) {
        if (typeof (window as any).Capacitor !== 'undefined' && (window as any).Capacitor.Plugins?.CKClipboardPlugin) {
          try {
            await (window as any).Capacitor.Plugins.CKClipboardPlugin.setClipboard({ text: decryptedText });
            this.lastCopiedText = decryptedText;
          } catch (e) {
            console.warn('[SyncEngine] Native setClipboard failed:', e);
          }
        }

        if (typeof navigator !== 'undefined') {
          if (document.hasFocus() && navigator.clipboard) {
            try {
              await navigator.clipboard.writeText(decryptedText);
              this.lastCopiedText = decryptedText;
            } catch {
              this.pendingClipboardText = decryptedText;
            }
          } else {
            this.pendingClipboardText = decryptedText;

            if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
              const preview = decryptedText.length > 80 ? decryptedText.substring(0, 80) + '...' : decryptedText;
              const notif = new Notification('CK Share • Clip Received', {
                body: `From ${msg.metadata.senderDeviceName}: "${preview}"\nClick to focus & copy.`,
                icon: '/icon-192.png',
                silent: true
              });
              notif.onclick = async () => {
                window.focus();
                if (navigator.clipboard) {
                  await navigator.clipboard.writeText(decryptedText);
                  this.pendingClipboardText = null;
                }
                notif.close();
              };
            }
          }
        }
      }
    } catch (decryptErr: any) {
      console.error('[SyncEngine] Decryption failed:', decryptErr);
      this.callbacks.onError('Failed to decrypt remote clip. Please verify the Room Access Code.');
      sound.playError();
    }
  }

  /**
   * Push a new clipboard text or code snippet to room
   */
  public async pushClipboard(text: string, isQuickDrop: boolean = false, fileName?: string, fileMimeType?: string): Promise<ClipItem> {
    if ((!this.ws || this.ws.readyState !== WebSocket.OPEN) && this.dataChannels.size === 0) {
      throw new Error('Not connected to a room');
    }
    if (this.settings.pauseSync) {
      throw new Error('Sync is currently paused');
    }
    if (!text) {
      throw new Error('Cannot push empty clip');
    }

    const type = fileName ? (fileMimeType?.startsWith('image/') ? 'image' : 'file') : this.detectClipType(text);
    const clipId = 'clip_' + Math.random().toString(36).substring(2, 10) + '_' + Date.now();
    this.receivedClipIds.add(clipId);

    const metadata: ClipMetadata = {
      id: clipId,
      senderDeviceId: this.device.deviceId,
      senderDeviceName: this.device.deviceName,
      senderOs: this.device.os,
      timestamp: Date.now(),
      type,
      sizeBytes: new TextEncoder().encode(text).length,
      fileName,
      fileMimeType
    };

    // Encrypt payload before transmitting over network
    const encrypted = await encryptPayload(text, this.accessCode);

    const message: RelayMessage = {
      type: isQuickDrop ? 'QUICK_DROP_PUSH' : 'CLIPBOARD_PUSH',
      roomId: this.roomId,
      metadata,
      encrypted,
      timestamp: Date.now()
    };
    const payloadStr = JSON.stringify(message);

    // 1. Send via Direct WebRTC DataChannels if open (Layer 3)
    let sentOverP2P = false;
    for (const dc of this.dataChannels.values()) {
      if (dc.readyState === 'open') {
        try {
          dc.send(payloadStr);
          sentOverP2P = true;
        } catch {}
      }
    }

    // 2. Send via Primary Cloudflare Hibernated WebSocket (Layer 1)
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(payloadStr);
    }

    // 3. Broadcast to Decentralized Nostr Relays (Layer 2)
    this.nostrPool.publish(payloadStr).catch(() => {});

    sound.playCopy();

    const localClip: ClipItem = {
      id: clipId,
      metadata,
      content: text,
      receivedAt: Date.now(),
      isLocal: true,
      isPinned: false
    };

    this.callbacks.onClipReceived(localClip);
    return localClip;
  }
}
